"use client";

import { useEffect, useRef, useState } from "react";

// @rhwp/core (Rust+WASM) 로 HWP/HWPX 를 브라우저에서 인라인 렌더.
//   /api/support-files/[id]/raw 에서 파일 바이트를 받아 페이지별 HTML 로 렌더한다.
export function HwpViewerClient({ fileId }: { fileId: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [pages, setPages] = useState(0);

  useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        setStatus("loading");
        const rhwp = await import("@rhwp/core");
        // WASM 초기화 (public/rhwp_bg.wasm)
        await rhwp.default("/rhwp_bg.wasm");
        try {
          rhwp.init_panic_hook();
        } catch {
          /* 무시 */
        }

        const res = await fetch(`/api/support-files/${fileId}/raw`);
        if (!res.ok) throw new Error(`파일 로드 실패 (${res.status})`);
        const bytes = new Uint8Array(await res.arrayBuffer());
        if (disposed) return;

        const doc = new rhwp.HwpDocument(bytes);
        const viewer = new rhwp.HwpViewer(doc);
        const count = viewer.pageCount();
        const html: string[] = [];
        for (let i = 0; i < count; i++) {
          try {
            // SVG 렌더 사용 — renderPageHtml 은 중첩 컨테이너(page-body>column>text-line>text-run)를
            //   전부 position:absolute + '페이지 절대좌표'로 내보내 offset 이 누적(compound)돼 글이
            //   오른쪽·아래로 밀리는 버그가 있다. SVG 는 viewBox 단일 좌표계라 누적이 없고 width:100% 로
            //   반응형 스케일도 깔끔하다. (rhwp README 권장 렌더러)
            html.push(viewer.renderPageSvg(i));
          } catch (e) {
            console.warn(`page ${i} render fail`, e);
          }
        }
        if (disposed) return;
        if (containerRef.current) {
          containerRef.current.innerHTML = html.join("\n");
        }
        setPages(count);
        setStatus("ready");
      } catch (e) {
        if (disposed) return;
        setErr((e as Error).message);
        setStatus("error");
      }
    })();
    return () => {
      disposed = true;
    };
  }, [fileId]);

  return (
    <div>
      {status === "loading" && (
        <p className="text-sm text-gray-400">HWP 뷰어 로딩 중… (최초 1회 WASM 로드)</p>
      )}
      {status === "error" && (
        <p className="text-sm text-red-600">
          미리보기 실패: {err}{" "}
          <a href={`/api/support-files/${fileId}/raw?dl=1`} className="text-blue-600 hover:underline">
            파일 다운로드
          </a>
        </p>
      )}
      {status === "ready" && (
        <p className="mb-2 text-xs text-gray-400">{pages} 페이지</p>
      )}
      <div
        ref={containerRef}
        className="hwp-viewer bg-white text-black [&_svg]:block [&_svg]:w-full [&_svg]:h-auto [&_svg]:mb-3 [&_svg]:border [&_svg]:border-gray-200"
        style={{ display: status === "ready" ? "block" : "none" }}
      />
    </div>
  );
}
